import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { SnackService } from '@app/components/shared/snack/snack.service';
import {
  SignalRLoadTestConfig,
  SignalRLoadTestService,
  SignalRLoadTestState,
} from '@app/services/signal-r/signal-r-load-test.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'faxe-rag',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatButton,
    MatCard,
    MatFormField,
    MatInput,
    MatLabel,
  ],
  templateUrl: './rag.component.html',
  styleUrl: './rag.component.scss'
})
export class RagComponent implements OnInit, OnDestroy {
  readonly form = new FormGroup({
    chatHubUrl: new FormControl('https://backend1-st.faxeit.se/hubs/ChatHub/', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    connectionCount: new FormControl(1000, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1)],
    }),
    delayMs: new FormControl(50, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0)],
    }),
  });

  state: SignalRLoadTestState = this.signalRLoadTestService.currentState;
  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private readonly signalRLoadTestService: SignalRLoadTestService,
    private readonly snackService: SnackService
  ) {
  }

  ngOnInit(): void {
    this.signalRLoadTestService.state$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(state => {
        this.state = state;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async toggleLoadTest(): Promise<void> {
    if (this.state.isRunning) {
      await this.signalRLoadTestService.stopLoadTest();
      this.snackService.showSnackBar('SignalR load test stopped');
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackService.showSnackBar('Please provide valid load test values');
      return;
    }

    try {
      await this.signalRLoadTestService.startLoadTest(this.form.getRawValue() as SignalRLoadTestConfig);
      this.snackService.showSnackBar('SignalR load test started');
    } catch (error) {
      this.snackService.showSnackBar(error instanceof Error ? error.message : 'Failed to start SignalR load test');
    }
  }

  async resetStatisticsAndLogs(): Promise<void> {
    await this.signalRLoadTestService.resetStatisticsAndLogs();
    this.snackService.showSnackBar('SignalR load test statistics and logs cleared');
  }

  downloadLogs(): void {
    const content = this.signalRLoadTestService.getLogFileContent();

    if (!content) {
      return;
    }

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const anchor = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);

    anchor.href = objectUrl;
    anchor.download = this.signalRLoadTestService.getLogFileName();
    anchor.click();

    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 0);
  }
}
